<template>
  <div class="bg-white w-full">
    <div class="p-4">
      <!-- Email Header -->
      <div class="mb-4">
        <div class="w-full mb-2">
          <v-text-field
            label="Subject"
            v-model="subject"
            readonly
          ></v-text-field>
        </div>
        <div class="w-full mb-2">
          <v-text-field
            v-model="formattedDate"
            label="Date"
            readonly
          ></v-text-field>
        </div>
      </div>

      <!-- Email Content -->
      <div class="mb-4">
        <div>Content:</div>
        <div class="email-content" v-html="content"></div>
      </div>

      <!-- Attachments -->
      <div v-if="hasAttachments" class="border-2 border-gray-200 p-2">
        <div class="font-bold mb-2">Attachments:</div>
        <div>
          <div
            v-for="(attachmentPath, i) in attachments"
            :key="i"
            class="w-full"
          >
            <div v-if="isPDF(attachmentPath)" class="my-4">
              <!-- PDF filename and actions -->
              <div
                class="flex items-center justify-between mb-4 bg-gray-50 p-3 rounded"
              >
                <span class="text-sm text-gray-600">
                  {{ getFileName(attachmentPath) }}
                </span>
              </div>

              <!-- PDF Viewer using direct Supabase URL -->
              <iframe
                :src="`${supabaseUrl}/storage/v1/object/public/ticket-attachments/${getFileName(attachmentPath)}`"
                type="application/pdf"
                class="w-full h-[600px] border border-gray-200 rounded"
                :key="attachmentPath"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import axios from "axios";

export default {
  name: "EmailContent",
  props: {
    selected_email_row: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  data() {
    return {
      subject: "",
      content: "",
      date: null,
      supabaseUrl: process.env.VUE_APP_SUPABASE_URL || 'https://your-project.supabase.co', // Add your Supabase URL here
    };
  },
  computed: {
    hasAttachments() {
      return this.selected_email_row?.attachments?.length > 0;
    },
    attachments() {
      if (!this.selected_email_row?.attachments) return [];
      const paths = this.selected_email_row.attachments
        .split(",")
        .filter(Boolean);
      return paths;
    },
    formattedDate() {
      return this.date ? moment(this.date).format("LLLL") : "";
    },
  },
  watch: {
    selected_email_row: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.subject = newVal.subject || "";
          this.content = newVal.content || "";
          this.date = newVal.sent_date ? moment(newVal.sent_date) : null;
        }
      },
    },
  },
  methods: {
    getFileName(path) {
      if (!path) return '';
      // Handle both path and direct filename cases
      const filename = path.includes('/') ? path.split('/').pop() : path;
      return filename;
    },

    sanitizeFileName(filename) {
      // First decode any URL-encoded characters
      const decodedName = decodeURIComponent(filename);
      // Keep the timestamp prefix intact
      const parts = decodedName.split('-');
      const timestamp = parts[0];
      const restOfName = parts.slice(1).join('-');
      
      // Sanitize only the part after the timestamp
      const sanitizedRest = restOfName
        .replace(/[,\s]/g, '_')  // Replace commas and spaces with underscore
        .replace(/[^a-zA-Z0-9._-]/g, '') // Remove any other illegal characters
        .replace(/_+/g, '_')  // Replace multiple underscores with single one
        .replace(/^_+|_+$/g, ''); // Remove leading/trailing underscores
      
      return `${timestamp}-${sanitizedRest}`;
    },

    isPDF(filename) {
      if (!filename) return false;
      const name = this.getFileName(filename).toLowerCase();
      return name.endsWith('.pdf');
    },
    async sendEmail() {
      try {
        // Check attachment sizes if there are attachments
        if (this.$refs.fileInput && this.$refs.fileInput.files.length > 0) {
          const files = this.$refs.fileInput.files;
          const maxLimit = 63; // 63MB limit
          const totalSize = Array.from(files).reduce((sum, file) => sum + file.size, 0);
          const maxSizeInBytes = maxLimit * 1024 * 1024; // Convert MB to bytes

          if (totalSize > maxSizeInBytes) {
            alert(`Total file size exceeds the maximum limit of ${maxLimit}MB`);
            return;
          }

          // Check individual file sizes
          for (const file of files) {
            if (file.size > maxSizeInBytes) {
              alert(`File "${file.name}" exceeds the maximum limit of ${maxLimit}MB`);
              return;
            }
          }
        }

        const formData = new FormData();
        // ... your existing formData setup ...

        const baseUrl = process.env.NODE_ENV === 'production'
          ? process.env.VUE_APP_PROD_SERVER_URL
          : 'http://localhost:7002';

        // First send the email
        await axios({
          method: 'post',
          url: `${baseUrl}/tickets/email_notification`,
          data: formData,
          headers: { 
            'Content-Type': 'multipart/form-data'
          },
          withCredentials: true
        });

        // Then create history record
        await axios.post(`${baseUrl}/history/add`, {
          ticket_id: this.selected_email_row.ticket_id,
          subject: this.subject,
          content: this.content,
          kaction: 'Email Sent',
          user_id: this.$store.state.user.user_id,
          attachments: this.attachments ? this.attachments.join(',') : null
        });

        // Handle success
        console.log('Email sent and history recorded successfully');
        
        // Optionally refresh the ticket history or emit an event
        this.$emit('email-sent');
        
      } catch (error) {
        console.error('Error in email process:', error);
        // Handle error appropriately
      }
    }
  },
};
</script>

<style scoped>
.email-content {
  padding: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  min-height: 200px;
}

.email-content p {
  line-height: 1.5;
  margin-bottom: 0.5rem;
}

iframe {
  background: white;
}
</style>
